'use client';

import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import type { ObjectComponent } from '@sizzlenz/shared';
import {
  OBJECT_COMPONENT_TYPE_OPTIONS,
  OBJECT_COMPONENT_CATEGORY_OPTIONS,
  OBJECT_COMPONENT_COMPENSATION_OPTIONS,
  OBJECT_COMPONENT_MARKETPLACE_VISIBILITY_OPTIONS,
  OBJECT_COMPONENT_STATUS_OPTIONS,
} from '@sizzlenz/shared';

interface ObjectComponentFormData {
  type: ObjectComponent['type'];
  title: string;
  category: ObjectComponent['category'];
  compensation: ObjectComponent['compensation'];
  details: string;
  needsProvider: boolean;
  marketplaceVisibility: ObjectComponent['marketplaceVisibility'];
  status: ObjectComponent['status'];
}

interface ObjectComponentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: ObjectComponent | null;
  onSave: (data: ObjectComponentFormData) => void;
}

export function ObjectComponentForm({
  open,
  onOpenChange,
  initialData,
  onSave,
}: ObjectComponentFormProps) {
  const isEditing = !!initialData;

  const { register, handleSubmit, control, reset } = useForm<ObjectComponentFormData>({
    defaultValues: {
      type: initialData?.type || 'requirement',
      title: initialData?.title || '',
      category: initialData?.category || 'venue',
      compensation: initialData?.compensation || 'flexible',
      details: initialData?.details || '',
      needsProvider: initialData?.needsProvider ?? true,
      marketplaceVisibility: initialData?.marketplaceVisibility || 'private',
      status: initialData?.status || 'draft',
    },
  });

  // Reset form when dialog opens with new data
  React.useEffect(() => {
    if (open) {
      reset({
        type: initialData?.type || 'requirement',
        title: initialData?.title || '',
        category: initialData?.category || 'venue',
        compensation: initialData?.compensation || 'flexible',
        details: initialData?.details || '',
        needsProvider: initialData?.needsProvider ?? true,
        marketplaceVisibility: initialData?.marketplaceVisibility || 'private',
        status: initialData?.status || 'draft',
      });
    }
  }, [open, initialData, reset]);

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit((data) => {
      onSave(data);
      onOpenChange(false);
    })(e);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Component' : 'Add Component'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onFormSubmit} className="space-y-4">
          {/* Type */}
          <div className="space-y-2">
            <Label>Type</Label>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OBJECT_COMPONENT_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="comp-title">Title *</Label>
            <Input
              id="comp-title"
              {...register('title', { required: true })}
              placeholder="e.g. Venue, Catering Provider"
              required
            />
          </div>

          {/* Category + Compensation row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OBJECT_COMPONENT_CATEGORY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label>Compensation</Label>
              <Controller
                name="compensation"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OBJECT_COMPONENT_COMPENSATION_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* Status + Marketplace Visibility row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OBJECT_COMPONENT_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label>Marketplace Visibility</Label>
              <Controller
                name="marketplaceVisibility"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OBJECT_COMPONENT_MARKETPLACE_VISIBILITY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2">
            <Label htmlFor="comp-details">Details</Label>
            <Textarea
              id="comp-details"
              {...register('details')}
              placeholder="Describe what's needed..."
              rows={3}
            />
          </div>

          {/* Needs Provider */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="needsProvider">Needs Provider</Label>
              <p className="text-sm text-muted-foreground">
                Looking for a provider to fulfil this component
              </p>
            </div>
            <Controller
              name="needsProvider"
              control={control}
              render={({ field }) => (
                <Switch id="needsProvider" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button type="submit" className="cursor-pointer">
              {isEditing ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
